import { computed, ref, toRefs, watch } from 'vue';
import ApplicationDetailsReview from '@/components/config_components/ApplicationDetailsReview/ApplicationDetailsReview.vue';

const previewableExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.doc', '.docx'];
const allTabs = [
  { id: 'manual-review-checklist', label: '1. Manual Review' },
  { id: 'manual-documents', label: '2. Documents' },
  { id: 'manual-applicant-information', label: '3. Applicant Information' }
];

function formatText(value, fallback = '-') {
  if (value === undefined || value === null) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

/**
 * @param {unknown} value
 * @returns {Record<string, unknown>}
 */
function asRecord(value) {
  return value && typeof value === 'object' ? /** @type {Record<string, unknown>} */ (value) : {};
}

/**
 * @param {unknown} value
 * @returns {unknown[]}
 */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * @param {unknown} value
 * @param {number} index
 * @returns {{
 *   id: string | number,
 *   name: string,
 *   file_name: string,
 *   url: string,
 *   status_label: string,
 *   status_class: string
 * }}
 */
function normalizeDocument(value, index) {
  const document = asRecord(value);
  return {
    id: document.id || `manual-history-document-${index}`,
    name: formatText(document.name || document.label || document.question || document.section || 'Document', 'Document'),
    file_name: formatText(document.file_name || document.name || '', ''),
    url: String(document.url || document.file_url || document.presigned_url || '').trim(),
    status_label: formatText(document.status_label || document.status || 'Submitted', 'Submitted'),
    status_class: String(document.status_class || 'bg-light text-muted')
  };
}

/**
 * @param {unknown[]} visibleTabs
 * @param {boolean} showHistoryTabs
 * @returns {{ id: string, label: string }[]}
 */
function buildAvailableTabs(visibleTabs, showHistoryTabs) {
  const requestedTabs = asArray(visibleTabs)
    .map((tab) => String(tab || '').trim())
    .filter(Boolean);

  if (requestedTabs.length) {
    return allTabs.filter((tab) => requestedTabs.includes(tab.id));
  }

  if (!showHistoryTabs) {
    return [{ id: 'manual-review-checklist', label: '1. Manual Review' }];
  }

  return allTabs;
}

function formatDetailValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not answered';
  return formatText(value, 'Not answered');
}

function formatDateOnly(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return raw.slice(0, 10);
}

function normalizeFieldRunStatus(fieldRun) {
  const normalizedFieldRun = asRecord(fieldRun);
  const value = String(normalizedFieldRun.match_status || '').trim().toLowerCase();
  if (value === 'match') return 'pass';
  if (value === 'mismatch') return 'fail';
  return 'na';
}

function questionHasPreview(item) {
  const normalizedItem = asRecord(item);
  const previewTarget = String(normalizedItem.file_url || normalizedItem.answer || '').toLowerCase();
  return previewableExtensions.some((extension) => previewTarget.includes(extension));
}

function buildManualHistoryComputedRefs(refs) {
  return {
    available_tabs: computed(() => buildAvailableTabs(refs.visibleTabs.value, Boolean(refs.showHistoryTabs.value))),
    applicant_form_sections: computed(() => asArray(refs.applicantFormSections.value)),
    normalized_documents: computed(() => asArray(refs.documents.value).map((document, index) => normalizeDocument(document, index))),
    normalized_review_sections: computed(() => asArray(refs.reviewSections.value)),
    normalized_field_runs: computed(() => asArray(refs.fieldRuns.value)),
    normalized_status_label: computed(() => formatText(refs.statusLabel.value, 'Completed'))
  };
}

function syncActiveTab(active_tab, tabs = []) {
  if (!tabs.length) {
    active_tab.value = 'manual-review-checklist';
    return;
  }

  if (!tabs.some((tab) => tab.id === active_tab.value)) {
    active_tab.value = tabs[0].id;
  }
}

function buildManualHistorySetup(props) {
  const refs = toRefs(props);
  const computedRefs = buildManualHistoryComputedRefs(refs);
  const active_tab = ref(computedRefs.available_tabs.value[0]?.id || 'manual-review-checklist');

  watch(
    computedRefs.available_tabs,
    (tabs) => syncActiveTab(active_tab, tabs),
    { immediate: true }
  );

  return {
    active_tab,
    ...computedRefs,
    format_date_only: formatDateOnly,
    format_detail_value: formatDetailValue,
    normalize_field_run_status: normalizeFieldRunStatus,
    question_has_preview: questionHasPreview
  };
}

export default {
  name: 'ManualVerificationHistoryView',
  components: {
    ApplicationDetailsReview
  },
  emits: ['preview-document', 'preview-question'],
  props: {
    decisionAction: { type: String, default: '' },
    decisionActor: { type: String, default: '' },
    decisionComment: { type: String, default: '' },
    decisionLabel: { type: String, default: '' },
    decisionTone: { type: String, default: 'secondary' },
    decisionTimestamp: { type: String, default: '' },
    documents: { type: Array, default: () => [] },
    applicantFormSections: { type: Array, default: () => [] },
    reviewSections: { type: Array, default: () => [] },
    fieldRuns: { type: Array, default: () => [] },
    showHistoryTabs: { type: Boolean, default: true },
    visibleTabs: { type: Array, default: () => [] },
    statusLabel: { type: String, default: 'Completed' }
  },
  setup(props) {
    return buildManualHistorySetup(props);
  }
};
